import React, {useEffect, useState} from 'react';
import {
    DefaultButton,
    IconButton,
    MessageBar,
    MessageBarType,
    Modal,
    PrimaryButton,
    Stack,
    Text,
    TextField
} from '@fluentui/react';
import {toast} from 'react-toastify';
import useCan, {STOREFRONT_TEMPLATES_GLOBAL} from '../../../permissions/ability';
import {useSaveStorefrontTemplateMutation} from '../../../store/endpoints/StorefrontTemplateEndpoints';
import TemplateGalleryEditor from './TemplateGalleryEditor';
import TemplateVisibilityFields, {ITemplateVisibilityValue} from './TemplateVisibilityFields';

interface ISaveStorefrontTemplateModalProps {
    isOpen: boolean;
    onDismiss: () => void;
    // The source storefront the template is captured from.
    storefront: {id: string; name: string};
    onSaved?: (templateId: string) => void;
}

// Two-step "save this storefront as a template" flow: step 1 captures the details and creates the
// template; step 2 lets the user attach preview images to its gallery.
const SaveStorefrontTemplateModal: React.FC<ISaveStorefrontTemplateModalProps> = (props) => {
    const {isOpen, onDismiss, storefront, onSaved} = props;

    const canGlobal = useCan(STOREFRONT_TEMPLATES_GLOBAL);

    const [saveTemplate] = useSaveStorefrontTemplateMutation();

    const [step, setStep] = useState<'details' | 'gallery'>('details');
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [visibility, setVisibility] = useState<ITemplateVisibilityValue>({
        isSystem: false,
        organizations: [],
        productLevel: [],
        entitlements: []
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const [savedTemplateId, setSavedTemplateId] = useState<string | undefined>();

    useEffect(() => {
        if (isOpen) {
            setStep('details');
            setName(`${storefront.name} Template`);
            setDescription('');
            setVisibility({isSystem: false, organizations: [], productLevel: [], entitlements: []});
            setSaving(false);
            setError(undefined);
            setSavedTemplateId(undefined);
        }
    }, [isOpen, storefront.name]);

    const onSave = async () => {
        if (!name.trim()) {
            setError('Template name is required');
            return;
        }
        setSaving(true);
        setError(undefined);
        try {
            const res = await saveTemplate({
                sourceStorefrontId: storefront.id,
                name: name.trim(),
                description: description.trim() || undefined,
                isSystem: canGlobal ? visibility.isSystem : undefined,
                organizations: canGlobal && visibility.organizations.length > 0 ? visibility.organizations : undefined,
                productLevel: canGlobal && visibility.productLevel.length > 0 ? visibility.productLevel : undefined,
                entitlements: canGlobal && visibility.entitlements.length > 0 ? visibility.entitlements : undefined
            }).unwrap();
            if (res.success && res.id) {
                toast.success('Template saved');
                setSavedTemplateId(res.id);
                setStep('gallery');
            } else {
                setError(res.message || 'Failed to save template');
                toast.error(res.message || 'Failed to save template');
            }
        } catch {
            setError('Failed to save template');
            toast.error('Failed to save template');
        }
        setSaving(false);
    };

    const onDone = () => {
        if (savedTemplateId) onSaved?.(savedTemplateId);
        onDismiss();
    };

    return (
        <Modal isOpen={isOpen} onDismiss={onDismiss} isBlocking={true}>
            <Stack tokens={{childrenGap: 12}} style={{padding: 24, width: 560, maxWidth: '90vw'}}>
                <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                    <Text variant="xLarge">
                        {step === 'details' ? 'Save as Template' : 'Add Preview Images'}
                    </Text>
                    <IconButton iconProps={{iconName: 'Cancel'}} ariaLabel="Close" onClick={onDismiss}/>
                </Stack>

                {step === 'details' && (
                    <Stack tokens={{childrenGap: 12}}>
                        <MessageBar messageBarType={MessageBarType.info}>
                            The template captures this storefront's look and feel, banners, sections, links, and
                            questions. Products are not included.
                        </MessageBar>
                        <TextField
                            label="Template Name"
                            required
                            value={name}
                            disabled={saving}
                            onChange={(_, v) => setName(v ?? '')}
                        />
                        <TextField
                            label="Description"
                            multiline
                            rows={3}
                            value={description}
                            disabled={saving}
                            onChange={(_, v) => setDescription(v ?? '')}
                        />
                        {canGlobal && (
                            <TemplateVisibilityFields
                                value={visibility}
                                disabled={saving}
                                onChange={(partial) => setVisibility(prev => ({...prev, ...partial}))}
                            />
                        )}
                        {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
                        <Stack horizontal horizontalAlign="end" tokens={{childrenGap: 8}}>
                            <DefaultButton text="Cancel" onClick={onDismiss} disabled={saving}/>
                            <PrimaryButton text="Save Template" onClick={onSave} disabled={saving}/>
                        </Stack>
                    </Stack>
                )}

                {step === 'gallery' && savedTemplateId && (
                    <Stack tokens={{childrenGap: 12}}>
                        <Text variant="small">
                            Optionally add preview images so this template is easy to recognize in the picker. You can
                            skip this and add them later.
                        </Text>
                        <TemplateGalleryEditor templateId={savedTemplateId}/>
                        <Stack horizontal horizontalAlign="end" tokens={{childrenGap: 8}}>
                            <PrimaryButton text="Done" onClick={onDone}/>
                        </Stack>
                    </Stack>
                )}
            </Stack>
        </Modal>
    );
};

export default SaveStorefrontTemplateModal;
